/**
 * @file tabs-assigned-customers.tsx
 * @module app/modules/sales-management/sales-manager/sales-representative/tabs/tabs-assigned-customers
 * @description Implements the tabs assigned customers interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import b1 from "../../../../../../_admin/assets/media/product/default.svg";
import { getKey, setKey } from '../../../../../../Global/history';
import Loader from '../../../../../../Global/loader';
import Pagination from '../../../../../../Global/pagination';
import { AllCustomers, PAGE_LIMIT } from '../../../../../../utils/constants';
import Method from '../../../../../../utils/methods';
import { listSalesRepresentativeCustomers } from '../../../../../../utils/storeString';
import APICallService from '../../../../../../api/apiCallService';
import { assistAdmin, buyer } from '../../../../../../api/apiEndPoints';
import { useNavigationHistory } from '../../../../../../utils/useNavigationHistory';
import ImageWithFallback from '../../../../_common/components/image-with-fallback';
const TabsAssignedCustomers = (props: any) => {
    const navigate = useNavigate();
    const { navigateTo } = useNavigationHistory();
    const [fetchLoading, setFetchLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [customers, setCustomers] = useState([]);
    const [page, setPage] = useState<any>(getKey(listSalesRepresentativeCustomers.page) || 1);
    const [pageLimit, setPageLimit] = useState<any>(
        getKey(listSalesRepresentativeCustomers.limit) || PAGE_LIMIT
    );
    useEffect(() => {
        setFetchLoading(true);
        (async () => {
            await fetchInitData(page, pageLimit);
        })();
        setFetchLoading(false);
    }, []);
    const fetchInitData = async (pageNo: number, limit: number) => {
        setLoading(true);
        const repId = props.representativeId ? props.representativeId : '';
        let params: any = {
          pageNo: pageNo,
          limit: limit,
          sortKey: "createdAt",
          sortOrder: -1,
          needCount: true,
          "assignedTo[0]": repId
        };
        let apiService = new APICallService(
          buyer.listOnBoardedBuyers,
          params,
          '',
          '',
          '',
          '',
          AllCustomers,
        );
        let apiResponse = await apiService.callAPI();
        if (apiResponse) {
            const mappedCustomers = apiResponse.records.map((c: any) => ({
                _id: c._id,
                name: c.name || (c.business && c.business.name) || "-",
                phone: `${c.phoneCountry} ${c.phone}` || "-",
                ordersPlaced:
                    c.analytics && c.analytics?.map((val: any) => val.totalOrders).reduce((a: number, b: number) => a + b, 0) || 0,
                totalSales:
                    c.analytics && c.analytics?.map((val: any) => val.totalOrderValue).reduce((a: number, b: number) => a + b, 0) || 0,
                status: c.blocked ? "Inactive" : "Active",
                image: (c.business && c.business.image),
            }));
            setCustomers(mappedCustomers);
            setTotalRecords(apiResponse.total);
        }
        setLoading(false);
    };

    const handleCurrentPage = async (val: number) => {
        setLoading(true);
        setPage(val);
        setKey(listSalesRepresentativeCustomers.page, val);
        await fetchInitData(val, pageLimit);
        setLoading(false);
    };
    const handleNextPage = async (val: number) => {
        setLoading(true);
        setPage(val + 1);
        setKey(listSalesRepresentativeCustomers.page, val + 1);
        await fetchInitData(val + 1, pageLimit);
        setLoading(false);
    };
    const handlePreviousPage = async (val: number) => {
        setLoading(true);
        setPage(val - 1);
        setKey(listSalesRepresentativeCustomers.page, val - 1);
        await fetchInitData(val - 1, pageLimit);
        setLoading(false);
    };
    const handlePageLimit = async (event: any) => {
        setLoading(true);
        setPage(1);
        setKey(listSalesRepresentativeCustomers.page, 1);
        setKey(listSalesRepresentativeCustomers.limit, parseInt(event.target.value));
        setPageLimit(parseInt(event.target.value));
        await fetchInitData(1, event.target.value);
        setLoading(false);
    };
    return (
        <>
            <Card className="border border-r10px">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                            <thead>
                                <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                                    <th className="min-w-125px">Customer Name</th>
                                    <th className="min-w-125px">No. of Order Places</th>
                                    <th className="min-w-125px">Total Sales</th>
                                    <th className="min-w-125px">Status</th>
                                    <th className="min-w-150px text-end"></th>
                                </tr>
                            </thead>
                            <tbody className="fs-15 fw-600">
                                {loading ? (
                                    <>
                                        <tr>
                                            <td colSpan={5}>
                                                <div className="d-flex justify-content-center">
                                                    <Loader loading={loading} />
                                                </div>
                                            </td>
                                        </tr>
                                    </>
                                ) : (
                                    <>
                                        {customers.length ? (
                                            customers.map((cust: any) => {
                                                return (
                                                    <>
                                                        <tr>
                                                            <td>
                                                                <span>
                                                                    <div className="d-flex align-items-center flex-row">
                                                                        <div className="symbol symbol-50px border position-relative">
                                                                            <ImageWithFallback
                                                                                className="img-fluid border-r8px object-fit-contain"
                                                                                src={
                                                                                    cust?.image
                                                                                        ? cust?.image
                                                                                        : b1
                                                                                }
                                                                                alt=""
                                                                                fallbackSrc={b1}
                                                                                noDefaultSize
                                                                            />
                                                                        </div>
                                                                        <span className="fs-15 fw-600 ms-3">
                                                                            {cust.name ? cust.name : "-"}
                                                                        </span>
                                                                    </div>
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span>
                                                                    {cust.ordersPlaced
                                                                        ? cust
                                                                            .ordersPlaced.toString()
                                                                            .padStart(2, "0")
                                                                        : 0}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span>
                                                                    {'TSh ' +
                                                                        Method.getGeneralizedAmount(
                                                                            cust.totalSales 
                                                                                ? cust
                                                                                    .totalSales
                                                                                : 0
                                                                        )}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`badge py-2 px-4 fs-14 rounded-pill ${cust.status === "Active"
                                                                        ? "bg-light-success text-success"
                                                                        : "bg-light-danger text-danger"
                                                                        }`}
                                                                >
                                                                    {cust.status}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                <Button
                                                                    className="uw-btn uw-btn-primary fs-14 fw-600"
                                                                    onClick={() => {
                                                                        let currentScrollY =
                                                                            window.scrollY;
                                                                        setKey(
                                                                            listSalesRepresentativeCustomers.scrollPosition,
                                                                            currentScrollY
                                                                        );
                                                                        navigateTo('/customers/customer-profile', {
                                                                            state: cust._id,
                                                                        })
                                                                    }}
                                                                >
                                                                    View details
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    </>
                                                );
                                            })
                                        ) : (
                                            <>
                                                {' '}
                                                <td colSpan={5}>
                                                    <div className="d-flex justify-content-center">
                                                        <span className="fs-18 fw-500">No Data Found</span>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card.Body>
            </Card>
            {!loading && customers && customers.length ? (
                <Pagination
                    totalRecords={totalRecords}
                    currentPage={page}
                    handleCurrentPage={handleCurrentPage}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    handlePageLimit={handlePageLimit}
                    pageLimit={pageLimit}
                />
            ) : (
                <></>
            )}
        </>
    );
};
export default TabsAssignedCustomers;
